import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Menu, ArrowLeft, ArrowUpRight } from 'lucide-react'
import { ModeToggle } from './DarkModeToggle'
import './navigation.css'

const navigationItems = [
  { name: 'About', hash: 'About' },
  { name: 'Thesis', hash: 'Thesis' },
  { name: 'Work', hash: 'Featured' },
  { name: 'Contact', hash: 'Contact' },
]

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('Home')
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'
  const isPortfolioRoute = location.pathname.startsWith('/portfolio')
  const isProjectPage = location.pathname.startsWith('/portfolio/')

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Track which front-page section is in view so the active link follows scroll.
  useEffect(() => {
    if (!isHomePage) return
    const ids = ['Home', ...navigationItems.map((i) => i.hash)]
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)
    if (targets.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible) setActiveSection(visible.target.id)
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: [0, 0.1, 0.25] }
    )
    targets.forEach((t) => observer.observe(t))
    return () => observer.disconnect()
  }, [isHomePage])

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return

    const hash = location.hash.slice(1)
    setActiveSection(hash)

    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(hash)
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)

    return () => window.clearTimeout(timeoutId)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (location.pathname !== '/portfolio' && location.pathname !== '/portfolio/') return

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }, [location.pathname])

  const handleNavClick = (hash: string) => {
    setActiveSection(hash)
    setIsOpen(false)

    if (location.pathname === '/') {
      setTimeout(() => {
        const element = document.getElementById(hash)
        if (hash === 'Home') {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 60)
    } else {
      navigate(hash === 'Home' ? '/' : `/#${hash}`)
    }
  }

  const handlePortfolioClick = () => {
    setIsOpen(false)
    navigate('/portfolio')

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }

  return (
    <header className={`site-nav-wrap ${isScrolled ? 'site-nav-scrolled' : ''}`}>
      <nav className="site-nav" aria-label="Primary">
        <button type="button" onClick={() => handleNavClick('Home')} className="site-nav-brand">
          <span className="site-nav-brand-name">Saad Zubairi</span>
          <span className="site-nav-brand-short" aria-hidden>
            SZ
          </span>
        </button>

        <ul className="site-nav-menu">
          {navigationItems.map((item) => (
            <li key={item.name}>
              <button
                type="button"
                className="site-nav-link"
                aria-current={isHomePage && activeSection === item.hash ? 'true' : undefined}
                onClick={() => handleNavClick(item.hash)}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>

        <div className="site-nav-actions">
          <button
            type="button"
            className="site-nav-portfolio"
            aria-current={isPortfolioRoute ? 'page' : undefined}
            onClick={handlePortfolioClick}
          >
            {isProjectPage ? <ArrowLeft aria-hidden /> : null}
            <span>Portfolio</span>
            {isProjectPage ? null : <ArrowUpRight aria-hidden />}
          </button>

          <ModeToggle />

          <div className="site-nav-mobile">
            <Drawer open={isOpen} onOpenChange={setIsOpen}>
              <DrawerTrigger asChild>
                <button type="button" className="site-nav-icon-button" aria-label="Open menu">
                  <Menu aria-hidden />
                </button>
              </DrawerTrigger>
              <DrawerContent className="site-nav-drawer">
                <DrawerHeader className="site-nav-drawer-head">
                  <DrawerTitle className="site-nav-drawer-title">Index</DrawerTitle>
                </DrawerHeader>
                <ul className="site-nav-drawer-list">
                  {[{ name: 'Home', hash: 'Home' }, ...navigationItems].map((item, i) => (
                    <li key={item.name}>
                      <button
                        type="button"
                        className="site-nav-drawer-link"
                        aria-current={isHomePage && activeSection === item.hash ? 'true' : undefined}
                        onClick={() => handleNavClick(item.hash)}
                      >
                        <span className="site-nav-drawer-no">{String(i).padStart(2, '0')}</span>
                        <span>{item.name}</span>
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      className="site-nav-drawer-link site-nav-drawer-portfolio"
                      aria-current={isPortfolioRoute ? 'page' : undefined}
                      onClick={handlePortfolioClick}
                    >
                      <span className="site-nav-drawer-no">
                        {String(navigationItems.length + 1).padStart(2, '0')}
                      </span>
                      <span>Portfolio</span>
                      <ArrowUpRight aria-hidden />
                    </button>
                  </li>
                </ul>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </nav>
    </header>
  )
}

export default Navigation
